import React from 'react';
import Head from 'next/head';

import useTranslation from 'hooks/use-translation';

import { getCityToDisplay } from 'shared/helpers/address';
import { useRemoteCommerceComponents } from 'src/theme/provider/commerce-components/remote-theme';
import { useHideSalesLanguage } from 'src/hooks/use-hide-sales-language';
import { useMetaInfo } from './use-meta-info';

import Meta from './meta';

// Keep titles descriptive, unique, and front-loaded with the page's primary entity.
const DutchiePageTitles = {
  about: 'About Dutchie | Cannabis Technology Platform',
  blog: 'Cannabis Industry Insights & News | Dutchie',
  cities: '{{retailerLabelPluralCapitalized}} by City | Dutchie',
  city: 'Cannabis {{retailerLabelPluralCapitalized}} in {{city}}, {{state}} | Dutchie',
  default: 'Order Cannabis Online for Pickup or Delivery | Dutchie',
  dispensary: '{{dispensaryName}} Menu | {{dispensaryCity}}, {{dispensaryState}} | Dutchie',
  dispensaryAllProducts: 'All Products at {{dispensaryName}} | Dutchie',
  dispensaryProduct: '{{productName}} at {{dispensaryName}} | Dutchie',
  dispensarySpecial: '{{specialName}} at {{dispensaryName}} | Dutchie',
  dispensarySpecials: '{{dispensaryName}} Deals & Offers | Dutchie',
  dispensaryBrands: 'Cannabis Brands at {{dispensaryName}} | Dutchie',
  dispensaryBrand: '{{brandName}} at {{dispensaryName}} | Dutchie',
  dispensaryInfo: '{{dispensaryName}} Hours, Address & Info | Dutchie',
  dispensaryProductCategories: 'Product Categories at {{dispensaryName}} | Dutchie',
  dispensaryProductCategory: '{{productCategory}} at {{dispensaryName}} | Dutchie',
  dispensaryProductSubcategory: '{{productSubcategory}} at {{dispensaryName}} | Dutchie',
  dispensarySearch: 'Search {{dispensaryName}} for {{searchTerm}} | Dutchie',
  concierge: 'AI Shopping Concierge | {{dispensaryName}}',
  careers: 'Careers at Dutchie | Join Our Team',
  help: 'Dutchie Help Center | Customer Support',
  home: 'Order Cannabis Online from Local {{retailerLabelPluralCapitalized}} | Dutchie',
  isolatedCheckout: 'Checkout | {{dispensaryName}}',
  news: 'Dutchie Newsroom | Company News & Updates',
  privacy: 'Privacy Policy | Dutchie',
  dispoTerms: '{{retailerLabelCapitalized}} Terms and Conditions | Dutchie',
  terms: 'Terms of Service | Dutchie',
  accessibility: 'Accessibility | Dutchie',
  messaging: 'Order Messaging | Dutchie',
  curbsideArrivals: 'Curbside Arrival | Dutchie',
  plus: 'Dutchie Plus | Enterprise Cannabis E-Commerce',
  whoops: 'Page Not Found | Dutchie',
};

const EmbeddedAndStoreFrontTitle = {
  ...DutchiePageTitles,
  dispensary: '{{dispensaryName}} Cannabis Menu | {{dispensaryCity}}, {{dispensaryState}}',
  dispensaryProduct: '{{productName}} at {{dispensaryName}}',
  dispensarySpecials: 'Deals & Offers | {{dispensaryName}}',
  dispensarySpecialsMA: 'Offers | {{dispensaryName}}',
  dispensarySpecial: '{{specialName}} | {{dispensaryName}}',
  dispensaryBrands: 'Cannabis Brands | {{dispensaryName}}',
  dispensaryBrand: '{{brandName}} | {{dispensaryName}}',
  dispensaryAllProducts: 'All Cannabis Products | {{dispensaryName}}',
  dispensaryProductCategories: 'Product Categories | {{dispensaryName}}',
  dispensaryProductCategory: '{{productCategory}} | {{dispensaryName}}',
  dispensaryProductSubcategory: '{{productSubcategory}} | {{dispensaryName}}',
  dispensarySearch: 'Search Results for {{searchTerm}} | {{dispensaryName}}',
};

export function useTitle(variant, location, isEmbeddedOrStoreFront) {
  const { t } = useTranslation();
  const metaInfo = useMetaInfo();
  const hideSalesLanguage = useHideSalesLanguage(metaInfo.dispensaryState);

  const useComplianceTitle = isEmbeddedOrStoreFront && hideSalesLanguage && variant === 'dispensarySpecials';
  const pageTitles = isEmbeddedOrStoreFront ? EmbeddedAndStoreFrontTitle : DutchiePageTitles;
  const resolvedVariant = pageTitles[variant] ? variant : `default`;

  const data = {
    defaultValue: pageTitles[useComplianceTitle ? 'dispensarySpecialsMA' : resolvedVariant],
    ...metaInfo,
  };

  if (location) {
    data.city = getCityToDisplay(location);
    data.state = location.state;
  }

  /* i18next-extract-disable-next-line */
  return t(`head.title.${resolvedVariant}`, data);
}

export default function PageTitle(props) {
  const { variant, location, isEmbeddedOrStoreFront } = props;
  const title = useTitle(variant, location, isEmbeddedOrStoreFront);
  const { remoteThemeModule } = useRemoteCommerceComponents();

  // Remote theme metadata is loaded asynchronously after hydration. Keep the core title in the
  // server-rendered response so crawlers and assistive technology always receive a document title;
  // the remote title replaces it client-side using the same Next.js head key.
  const hasCustomMetaFieldsFunction = !!remoteThemeModule.default?.getStoreFrontMetaFields;

  return (
    <>
      <Head>
        <title key='title'>{title}</title>
      </Head>
      {!hasCustomMetaFieldsFunction && (
        <>
          <Meta property='og:title' content={title} />
          <Meta property='twitter:title' content={title} />
        </>
      )}
    </>
  );
}
